import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {CreateFormInputDisplay} from '../../../../../../../../../../src/components/common/createFormInputs/createFormInputDisplay';
import {
  computeIsGlobalNamespace,
  globalNamespace,
} from '../../../../../../../../../../src/helpers/models/namespaceHelpers';
import {HandlerOf} from '../../../../../../../../../shared/helpers/src/react/reactHelpers';
import {SimpleDropdown} from '../../../../../../../../../shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../../../../shared/ui/src/components/dropdowns/simpleDropdownItem';
import {Icon} from '../../../../../../../../../shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../../../../../shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../../../../../shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface ImportContactsNamespaceDropdownProps {
  /** Current selected namespace. */
  namespace: string;
  /** The users namespace to add private contacts too. */
  userNamespace: string;

  onNamespaceSelect: HandlerOf<string>;
}

/*
 * Style.
 */

interface StyledButtonDivProps {
  $hasHover: boolean;
}
const StyledButtonDiv = styled.div<StyledButtonDivProps>`
  display: flex;
  flex-flow: row;
  border-radius: 8px;
  background: ${(p) => p.theme.greys.shade20};
  height: 30px;
  padding: 0 8px;
  align-items: center;

  &:hover {
    ${addHoverStyles()}
  }
  ${(p) => p.$hasHover && addHoverStyles()}
`;

function addHoverStyles() {
  return css`
    background: ${(p) => p.theme.greys.shade30};
  `;
}

const StyledNamespaceWrapperDiv = styled.div`
  flex: 1;
  display: flex;
  flex-flow: row;
  gap: 8px;
  line-height: 30px;
  align-items: center;
`;

const StyledSimpleDropdownItem = styled(SimpleDropdownItem)`
  display: flex;
  flex-flow: row;
  padding: 0 8px;
`;

/*
 * Component.
 */

export const ImportContactsNamespaceDropdown: FC<ImportContactsNamespaceDropdownProps> = (props) => {
  const theme = useUserTheme();
  const {namespace, userNamespace, onNamespaceSelect} = props;
  return (
    <CreateFormInputDisplay
      title={<Trans id="contacts-manager-import-contacts-import-to-title">Import to</Trans>}
      input={
        <PopoverCoordinator
          renderButton={(hasHover) => (
            <StyledButtonDiv $hasHover={hasHover}>
              {renderNamespaceContent(namespace)}
              <Icon name="chevronDownSmall" color={theme.greys.shade60} />
            </StyledButtonDiv>
          )}
          renderModal={(popoverProps) => (
            <SimpleDropdown {...popoverProps}>
              <StyledSimpleDropdownItem onSelect={() => onNamespaceSelect(globalNamespace)}>
                {renderNamespaceContent(globalNamespace)}
              </StyledSimpleDropdownItem>
              <StyledSimpleDropdownItem onSelect={() => onNamespaceSelect(userNamespace)}>
                {renderNamespaceContent(userNamespace)}
              </StyledSimpleDropdownItem>
            </SimpleDropdown>
          )}
        />
      }
      isRequired
    />
  );
};

/*
 * Helpers.
 */

function renderNamespaceContent(namespace: string) {
  if (computeIsGlobalNamespace(namespace)) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledNamespaceWrapperDiv>
            <Icon name="buildingFilledSmall" color={theme.palette.teal.shade40} />
            <Trans id="contacts-manager-import-contacts-import-company-contacts">Company contacts</Trans>
          </StyledNamespaceWrapperDiv>
        )}
      </UserThemeConsumer>
    );
  }
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledNamespaceWrapperDiv>
          <Icon name="lockFilledNewTiny" color={theme.greys.shade80} />
          <Trans id="contacts-manager-import-contacts-import-private-contacts">Private contacts</Trans>
        </StyledNamespaceWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}
